import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import {
  getContainerData,
  getSponsoredAssetData,
} from '#/redux/modules/brightcove/selectors';
import {
  loadSponsoredAsset,
  saveSponsoredVideos,
} from '#/redux/modules/brightcove/actions';
import {
  heroBannerEnabled,
  getEntryById,
} from '#/redux/modules/accedoOne/selectors';
import { getCurrentProfile } from '#/redux/modules/onboarding/selectors';
import {
  getCustomDataVideosBlocked,
  getCustomDataShowsBlocked,
} from '#/redux/modules/identity/selectors';

import getVw from '#/utils/getVw';
import imgixUrl from '#/utils/imgix';
import { subRoutes, mainRoutes } from '#/utils/routes';
import {
  containersTypeAlias,
  imgixTileResizeParameters,
  SWIMLANE_LENGTH,
  SWIMLANE_RIGHT_ARROW,
  SWIMLANE_LEFT_ARROW,
  mainPageNames,
} from '#/config/constants';

import VideoCard from '#/components/VideoCard/VideoCard';
import Swimlane from '#/components/Swimlane/Swimlane';
import styles from './playlistSwimlaneFactory.scss';
import { isWorkstation } from '#/utils/componentStyleConfig';
import { getDurationLabel, setSpeech } from '#/utils/accessibility';
import { isItemOutsideScreen } from '#/utils/isItemOutsideScreen';
import { FocusDiv } from '#/widgets/ui';

import { getComponentItems } from '../swimlaneFactoryUtils';
import { isContentBlocked } from '../../../utils/isContentBlocked';

const SeeAllCard = ({
  nav,
  onClick,
  onFocus,
  className,
  style,
  children,
  ariaLabel,
}) => {
  return (
    <FocusDiv
      nav={nav}
      onClick={onClick}
      onFocus={onFocus}
      className={className}
      classNameFocused="focused"
      style={style}
      ariaLabel={ariaLabel}
    >
      {children}
    </FocusDiv>
  );
};

SeeAllCard.propTypes = {
  nav: PropTypes.object,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.any,
  style: PropTypes.any,
  ariaLabel: PropTypes.string,
};

// playlist data loaded in Home
const PlaylistSwimlaneFactory = ({
  title,
  videos,
  containerId,
  playlistId,
  onTileClick,
  alwaysVisible = false,
  nav = { id: '' },
  ageTheme,
  enableSeeAll = true,
  className,
  history,
  onFocus,
  fromHome = false,
  location,
  useLastFocus = true,
  // enablePodcasts = false,
  // enableAllUpNextVideo = false,
  recentlyWatched = false,
  upnext = false,
  livestreamTileEntry,
  sponsoredTileEntry,
  livestreamTilePosition,
  sponsoredTilePosition,
  swimlaneSponsor,
}) => {
  const dispatch = useDispatch();
  const currentProfile = useSelector(getCurrentProfile);
  const customDataShowsBlocked = useSelector(getCustomDataShowsBlocked);
  const customDataVideosBlocked = useSelector(getCustomDataVideosBlocked);
  const items =
    videos ||
    useSelector(state => getContainerData(state, [containerId]))[containerId] ||
    [];

  const livestreamTileEntryData =
    useSelector(state => getEntryById(state, livestreamTileEntry)) || null;
  const sponsoredTileEntryData =
    useSelector(state => getEntryById(state, sponsoredTileEntry)) || null;
  const swimlaneSponsorData =
    useSelector(state => getEntryById(state, swimlaneSponsor)) || null;
  const backgroundImageUrls =
    useSelector(state =>
      getEntryById(state, swimlaneSponsorData?.backgroundImageUrls),
    ) || null;
  const sponsoredTileVideo =
    useSelector(state => getSponsoredAssetData(state)) || null;
  const { separatorColor, backgroundColor, pageLimit } =
    useSelector(state => getEntryById(state, containerId)) || {};
  const { enabled: bgPlayerDisabled } = useSelector(heroBannerEnabled);
  const getItemId = useCallback(index => `${nav.id}-VIDEO-${index}`, [nav?.id]);
  const SEE_ALL = `${nav.id}-see-all`;
  const currentIndex = useRef(0);
  const previousIndex = useRef(0);
  const currentPath = location?.pathname?.split('/').slice(-1)[0];

  const [rowChanged, setRowChanged] = useState(true);
  const itemFocusChanged = useRef(() => {});
  const setFocusChanged = useCallback(func => {
    itemFocusChanged.current = func;
  }, []);

  const handleNavigation = useCallback((nextIndex, itemsLength) => {
    if (nextIndex < 0 || nextIndex > itemsLength - 1) {
      return;
    }
    setRowChanged(false);
    currentIndex.current = nextIndex;
    return false;
  }, []);

  const onItemFocus = useCallback(
    id => {
      if (onFocus) {
        onFocus(nav.id);
      }

      // If no id then assume we're jumping to the first tile
      id = id ?? `${nav.id}-VIDEO-0`;

      previousIndex.current = currentIndex.current;
      currentIndex.current = id;

      itemFocusChanged.current(id);
    },
    [onFocus, nav.id, currentIndex],
  );

  useEffect(() => {
    previousIndex.current = 0;
    currentIndex.current = 0;

    if (!useLastFocus) {
      itemFocusChanged.current(getItemId(currentIndex.current));
    }
  }, [items]);

  const getNextRight = useCallback(
    i => {
      const itemsLimit = recentlyWatched
        ? pageLimit
        : items.limit || SWIMLANE_LENGTH;
      if (
        (recentlyWatched && i === items.length - 1 && i < itemsLimit - 1) ||
        (i === itemsLimit - 1 && enableSeeAll)
      ) {
        return { nextright: SEE_ALL };
      }

      return i !== items.length - 1 ? { nextright: getItemId(i + 1) } : {};
    },
    [items, recentlyWatched],
  );

  const generateItemNavs = useCallback(
    i => {
      return {
        parent: nav.id,
        id: getItemId(i),
        ...(i !== 0 ? { nextleft: getItemId(i - 1) } : {}),
        ...getNextRight(i),
        internal: {
          nextright: () => handleNavigation(i + 1, items.length),
          nextleft: () => handleNavigation(i - 1, items.length),
          nextup: () => setRowChanged(true),
          nextdown: () => setRowChanged(true),
        },
      };
    },
    [items],
  );

  useEffect(() => {
    if (sponsoredTileEntryData?.brightcoveVideoId) {
      dispatch(
        loadSponsoredAsset([
          { videoId: sponsoredTileEntryData?.brightcoveVideoId },
        ]),
      );
      dispatch(saveSponsoredVideos(sponsoredTileEntryData?.brightcoveVideoId));
    }
  }, [sponsoredTileEntryData?.brightcoveVideoId]);

  useEffect(() => {
    const itemsLimit = recentlyWatched
      ? pageLimit
      : items.limit || SWIMLANE_LENGTH;
    const currentItemId =
      currentIndex.current === itemsLimit && enableSeeAll
        ? SEE_ALL
        : getItemId(currentIndex.current);
    if (!isWorkstation() && isItemOutsideScreen(currentItemId)) {
      if (previousIndex.current < currentIndex.current) {
        document.getElementById(SWIMLANE_RIGHT_ARROW + nav.id)?.click();
      } else if (previousIndex.current > currentIndex.current) {
        document.getElementById(SWIMLANE_LEFT_ARROW + nav.id)?.click();
      }
    }
    previousIndex.current = currentIndex.current;
  }, [currentIndex, previousIndex, items, recentlyWatched]);

  const createAriaLabel = useCallback(
    item => {
      const label = `${item.name}, ${
        item?.custom_fields?.subtitle
      }  ${getDurationLabel(item?.duration)}`;
      if (rowChanged) {
        return `${title || 'Videos'}, ${label}`;
      }
      return label;
    },
    [rowChanged],
  );
  const HandleLiveStream = () => {
    const pathname = `${mainRoutes.LIVETV}/${currentProfile?.age}`;
    history.replace('/');
    history.push(pathname);
  };

  const [videoCards, emptyItems] = useMemo(() => {
    const itemsLimit = upnext // let upnext display all
      ? items.pageLimit
      : recentlyWatched
      ? pageLimit
      : items.limit || SWIMLANE_LENGTH;

    // Insert the live stream or sponsored video into the current swimlane at the given index position
    // Checks are to prevent it from adding duplicates or adding to an empty swimlane
    const sponseredItem = items;
    livestreamTileEntryData &&
      !(sponseredItem[livestreamTilePosition] === '') &&
      items?.length > 0 &&
      sponseredItem.splice(livestreamTilePosition, 0, '');
    sponsoredTileEntryData &&
      !(sponseredItem[sponsoredTilePosition] === sponsoredTileVideo) &&
      items?.length > 0 &&
      sponseredItem.splice(sponsoredTilePosition, 0, sponsoredTileVideo);

    if (
      livestreamTileEntryData &&
      sponsoredTileEntryData &&
      livestreamTilePosition >= sponsoredTilePosition
    ) {
      livestreamTilePosition += 1;
    }

    const [cards, emptyCards] = getComponentItems(
      sponseredItem.slice(0, itemsLimit),
      (item, index) => {
        const commonProps = {
          nav: generateItemNavs(index),
          key: getItemId(index),
        };

        if (livestreamTileEntryData && index === livestreamTilePosition) {
          return [
            <VideoCard
              {...commonProps}
              videoId={null}
              imgUrl={livestreamTileEntryData?.imageUrl}
              title={livestreamTileEntryData?.title}
              showTitle={livestreamTileEntryData?.displaySubtext}
              referenceId={null}
              duration={null}
              ariaLabel="Live Tile"
              roundedCorners={ageTheme?.tileRounded}
              onClick={HandleLiveStream}
              onItemFocus={onItemFocus}
              className={classNames(styles.seeAllContainer, currentPath || '', {
                [styles.isTv]: !isWorkstation(),
              })}
              fromHome={fromHome}
              blocked={false}
              livestreamTileEntryData={livestreamTileEntryData}
            />,
            <FocusDiv
              {...commonProps}
              onFocus={onItemFocus}
              classNameFocused={styles.emptyFocused}
              className={classNames(
                styles.seeAllContainer,
                styles.empty,
                currentPath || '',
                {
                  [styles.isTv]: !isWorkstation(),
                },
              )}
            />,
          ];
        }
        if (
          sponsoredTileEntryData &&
          index === sponsoredTilePosition &&
          sponsoredTileVideo
        ) {
          return [
            <VideoCard
              {...commonProps}
              videoId={
                bgPlayerDisabled ? undefined : Number(sponsoredTileVideo?.id)
              }
              imgUrl={
                sponsoredTileVideo?.custom_fields?.episode_key_art_horizontal ||
                sponsoredTileVideo?.custom_fields?.episode_key_art_square ||
                sponsoredTileVideo?.thumbnail
              }
              title={sponsoredTileVideo?.name}
              showTitle={sponsoredTileVideo?.custom_fields?.subtitle}
              duration={sponsoredTileVideo?.duration}
              ariaLabel={createAriaLabel(sponsoredTileVideo)}
              roundedCorners={ageTheme?.tileRounded}
              onClick={onTileClick(sponsoredTileVideo, index)}
              onItemFocus={onItemFocus}
              className={classNames(styles.seeAllContainer, currentPath || '', {
                [styles.isTv]: !isWorkstation(),
              })}
              referenceId={sponsoredTileVideo?.reference_id}
              fromHome={fromHome}
              blocked={false}
              sponsoredTileEntryData={sponsoredTileEntryData}
            />,
            <FocusDiv
              {...commonProps}
              onFocus={onItemFocus}
              classNameFocused={styles.emptyFocused}
              className={classNames(
                styles.seeAllContainer,
                styles.empty,
                currentPath || '',
                {
                  [styles.isTv]: !isWorkstation(),
                },
              )}
            />,
          ];
        }
        return [
          <VideoCard
            {...commonProps}
            videoId={bgPlayerDisabled ? undefined : Number(item.id)}
            imgUrl={
              item.custom_fields?.episode_key_art_horizontal || item.thumbnail
            }
            title={item.name}
            showTitle={item.custom_fields?.subtitle}
            duration={item.duration}
            ariaLabel={createAriaLabel(item)}
            roundedCorners={ageTheme?.tileRounded}
            onClick={item.onClick ? item.onClick : onTileClick(item, index)}
            onItemFocus={onItemFocus}
            className={classNames(styles.seeAllContainer, currentPath || '', {
              [styles.isTv]: !isWorkstation(),
            })}
            referenceId={item.reference_id}
            fromHome={fromHome}
            blocked={isContentBlocked(currentProfile, item)}
          />,
          <FocusDiv
            {...commonProps}
            onFocus={onItemFocus}
            classNameFocused={styles.emptyFocused}
            className={classNames(
              styles.seeAllContainer,
              styles.empty,
              currentPath || '',
              {
                [styles.isTv]: !isWorkstation(),
              },
            )}
          />,
        ];
      },
    );

    if (
      (items.length > itemsLimit && enableSeeAll) ||
      (items.length && recentlyWatched)
    ) {
      const path = history.location?.pathname?.split('/');

      let redirectpath;
      let thumbnail;
      let nextLeftId = itemsLimit - 1;

      if (recentlyWatched) {
        redirectpath = `${mainRoutes.RECENTLY_WATCHED}/${containerId}/${containersTypeAlias.CONTAINER_RECENTLY_WATCHED}`;
        thumbnail = items[0]?.thumbnail;
        items.length <= itemsLimit && (nextLeftId = items.length - 1);
      } else {
        const containerAlias =
          currentPath === mainPageNames.FAVORITES && !isWorkstation()
            ? containersTypeAlias.TVCONTAINER_FAV_VIDEOS
            : containersTypeAlias.CONTAINER_OVP_PLAYLIST;

        redirectpath = `/${path[1]}/${path[2]}/${subRoutes.SEE_ALL}/${items[itemsLimit].id}/${title}/${containerAlias}/${playlistId}`;

        thumbnail = items[itemsLimit]?.thumbnail;
      }

      emptyCards.push(
        <FocusDiv
          nav={{
            id: SEE_ALL,
            nextleft: getItemId(nextLeftId),
            parent: nav.id,
          }}
          key={SEE_ALL}
          onClick={() => {
            history.push(redirectpath);
          }}
          className={classNames(
            styles.seeAllContainer,
            styles.empty,
            currentPath || '',
            {
              [styles.isTv]: !isWorkstation(),
            },
          )}
          classNameFocused={styles.emptyFocused}
          style={{
            borderRadius: ageTheme?.tileRounded ? getVw(24) : '0px',
            outline: 'none',
          }}
          onFocus={() => {
            setSpeech(`See all, ${title}`);
            onItemFocus(SEE_ALL);
          }}
          ariaLabel="See All"
        />,
      );

      cards.push(
        <SeeAllCard
          nav={{
            id: SEE_ALL,
            nextleft: getItemId(nextLeftId),
            parent: nav.id,
          }}
          key={SEE_ALL}
          onClick={() => history.push(redirectpath)}
          className={classNames(styles.seeAllContainer, currentPath || '', {
            [styles.isTv]: !isWorkstation(),
          })}
          style={{
            borderRadius: ageTheme?.tileRounded ? getVw(24) : '0px',
            outline: 'none',
          }}
          onFocus={() => {
            setSpeech(`See all, ${title}`);
            onItemFocus(SEE_ALL);
          }}
          ariaLabel="See All"
        >
          <img
            aria-hidden="true"
            className={styles.seeAllImage}
            src={
              imgixUrl(
                items[itemsLimit]?.custom_fields?.episode_key_art_horizontal,
                imgixTileResizeParameters.VIDEOS,
              ) || thumbnail
            }
            alt=""
          />
          <div id={SEE_ALL} aria-hidden="true">
            <p>See all</p>
          </div>
        </SeeAllCard>,
      );
    }

    return [cards, emptyCards];
  }, [
    items,
    rowChanged,
    onItemFocus,
    recentlyWatched,
    upnext,
    customDataShowsBlocked,
    customDataVideosBlocked,
  ]);

  const videosSwimlaneNav = {
    ...nav,
    forwardFocus: `${nav.id}-VIDEO-0`,
    useLastFocus,
  };

  return videoCards.length ? (
    <Swimlane
      isTv={!isWorkstation()}
      alwaysVisible={alwaysVisible}
      className={className}
      items={videoCards}
      emptyItems={emptyItems}
      title={title}
      nav={videosSwimlaneNav}
      navId={nav.id}
      setFocusChanged={setFocusChanged}
      separatorColor={separatorColor}
      backgroundColor={backgroundColor}
      swimlaneSponsorData={swimlaneSponsorData}
      backgroundImageUrls={backgroundImageUrls}
    />
  ) : null;
};
PlaylistSwimlaneFactory.propTypes = {
  title: PropTypes.string,
  videos: PropTypes.array,
  containerId: PropTypes.string,
  playlistId: PropTypes.string,
  alwaysVisible: PropTypes.bool,
  onTileClick: PropTypes.func,
  nav: PropTypes.object,
  ageTheme: PropTypes.object,
  history: PropTypes.object,
  className: PropTypes.string,
  enableSeeAll: PropTypes.bool,
  onFocus: PropTypes.func,
  fromHome: PropTypes.bool,
  enablePodcasts: PropTypes.bool,
  enableAllUpNextVideo: PropTypes.bool,
  recentlyWatched: PropTypes.bool,
  location: PropTypes.object,
  useLastFocus: PropTypes.bool,
  upnext: PropTypes.bool,
  livestreamTileEntry: PropTypes.object,
  sponsoredTileEntry: PropTypes.object,
  swimlaneSponsor: PropTypes.object,
  livestreamTilePosition: PropTypes.number,
  sponsoredTilePosition: PropTypes.number,
};

export default withRouter(PlaylistSwimlaneFactory);
